import React, { useEffect, useMemo } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { useParams } from 'react-router-dom';
import { CompanyDocuments } from 'api';
import { documentGenerationJobsUrl, documentsUrl } from 'common/config/urls';
import { DOCUMENT_GENERATION_JOBS_SLUG, DOCUMENT_GENERATION_JOBS_TITLE } from 'common/constants/documents';
import { useStore } from 'common/store';
import { UseStoreValues } from 'common/types/store';
import type { TertiaryNavigationLinkItem } from 'context/FirmBaseLayoutContext';
import { TERTIARY_NAVIGATION_QUERY_PARAMS, useTertiaryNavigationRegistration } from 'context/FirmBaseLayoutContext';
import {
  TAB_ID_DOCUMENTS,
  TAB_ID_QUESTIONS,
  TAB_LABEL_DOCUMENTS,
  TAB_LABEL_QUESTIONS,
} from 'dashboard409a/pages/Information/CurrentRequestedDocumentsAndQuestions/types';
import { useDocuments } from 'services/hooks';
import DocumentGenerationJobPageHeader from './components/DocumentGenerationJobPageHeader/DocumentGenerationJobPageHeader';
import DocumentGenerationJobsTable from './components/DocumentGenerationJobTable/DocumentGenerationJobTable';

const DocumentGenerationJobs = () => {
  // any because companyInfo is not strongly typed
  const [storeValue] = useStore() as unknown as UseStoreValues;
  const { companyInfo } = storeValue;
  const { firmSlugParam = '', companySlugParam = '' } = useParams<{ firmSlugParam: string; companySlugParam: string }>();
  const { getDocuments, companyDocuments } = useDocuments() as unknown as {
    getDocuments: (companyId: number, measurementDataId: number) => Promise<void>;
    companyDocuments: CompanyDocuments[] | undefined;
  };

  useEffect(() => {
    if (companyInfo?.id) {
      getDocuments(companyInfo.id as number, companyInfo.measurement_data_id as number);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyInfo?.id]);

  // Mirror the Documents page tertiary set so the user keeps their place in the Documents section.
  const tertiaryNavState = useMemo(() => {
    const documentsTabUrl = (tabId: string) =>
      `${documentsUrl(firmSlugParam, companySlugParam)}?${TERTIARY_NAVIGATION_QUERY_PARAMS.tab}=${tabId}`;
    const items: TertiaryNavigationLinkItem[] = [
      { value: TAB_ID_DOCUMENTS, label: TAB_LABEL_DOCUMENTS, to: documentsTabUrl(TAB_ID_DOCUMENTS) },
      { value: TAB_ID_QUESTIONS, label: TAB_LABEL_QUESTIONS, to: documentsTabUrl(TAB_ID_QUESTIONS) },
      {
        value: DOCUMENT_GENERATION_JOBS_SLUG,
        label: DOCUMENT_GENERATION_JOBS_TITLE,
        to: documentGenerationJobsUrl(firmSlugParam, companySlugParam),
      },
    ];
    return { items, value: DOCUMENT_GENERATION_JOBS_SLUG };
  }, [firmSlugParam, companySlugParam]);

  useTertiaryNavigationRegistration(tertiaryNavState);

  if (!companyInfo?.id)
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress />
      </Box>
    );

  const hasDocuments = companyDocuments === undefined || companyDocuments.length > 0;

  return (
    <div>
      <DocumentGenerationJobPageHeader showCreateButton={hasDocuments} />
      <DocumentGenerationJobsTable companyDocuments={companyDocuments} />
    </div>
  );
};

export default DocumentGenerationJobs;
